import { useState } from 'react'

import OrderedPageFilter from 'core/application/commons/orderedPageFilter'

import { RangePickerV2 } from 'common/components'
import { formatDate } from 'common/dateFormatters'

import useSharedState from './useSharedState'

type FilteredInfoType = {
  [key: string]: { start: string; end: string } | string[]
}

const INITIAL_FILTER = {
  pageNumber: 1,
  pageSize: 10,
  orderColumn: 'createdAt',
  orderDescending: true,
}

const useTableFilter = (data: object[], name: string, defaultSortingPaginationFilter?: OrderedPageFilter) => {
  const [filteredInfo, setFilteredInfo] = useSharedState<FilteredInfoType>(`${name}-table-filter`, {})
  const [sortingPagination, setSortingPagination] = useSharedState<OrderedPageFilter>(
    `${name}-pagination`,
    defaultSortingPaginationFilter || INITIAL_FILTER,
  )
  const [visibleFilter, setVisibleFilter] = useState<string>('')

  const handleVisibleFilter = (filter: string) => (visible: boolean) => {
    setVisibleFilter(visible ? filter : '')
  }

  const handleFilterInfoChange = (filters, pagination) => {
    setFilteredInfo({ ...filteredInfo, ...filters })
    setSortingPagination(pagination)
  }

  const handleDateChange = (dataIndex: string, confirm: () => void) => (dates, dateStrings) => {
    confirm()
    if (dates) {
      setFilteredInfo({
        ...filteredInfo,
        [dataIndex]: { start: dates[0].format('YYYY-MM-DD'), end: dates[1].format('YYYY-MM-DD') },
      })
    } else {
      setFilteredInfo(
        (() => {
          const { [dataIndex]: _, ...rest } = filteredInfo
          return rest
        })(),
      )
    }
  }

  const dateFilterDropdown =
    (dataIndex) =>
    ({ setSelectedKeys, confirm, clearFilters }) => {
      const value = filteredInfo[dataIndex]
      return (
        !Array.isArray(value) && (
          <div className="p-8">
            <RangePickerV2
              onChange={handleDateChange(dataIndex, confirm)}
              value={value ? [formatDate(value.start), formatDate(value.end)] : null}
            />
          </div>
        )
      )
    }

  const onDateFilter = (dataIndex) => (value, record) => {
    const recordDate = formatDate(record[dataIndex])
    return recordDate.isSameOrAfter(formatDate(value.start)) && recordDate.isSameOrBefore(formatDate(value.end))
  }

  const filteredData =
    data &&
    data.filter((record) => {
      return Object.entries(filteredInfo).every(([dataIndex, value]) => {
        if (!value) {
          return true
        }
        return Array.isArray(value) ? value.includes(record[dataIndex]) : onDateFilter(dataIndex)(value, record)
      })
    })

  return {
    handleFilterInfoChange,
    dateFilterDropdown,
    filteredData,
    filteredInfo,
    sortingPaginationInfo: sortingPagination,
    visibleFilter,
    handleVisibleFilter,
  }
}

export default useTableFilter
